import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useMediaQuery } from '../../hooks';

const NAV = [
  { path: '/dashboard',   label: 'Dashboard',    icon: '◈' },
  { path: '/emails',      label: 'Emails',       icon: '✉' },
  { path: '/compose',     label: 'Compose',      icon: '+' },
  { path: '/analytics',   label: 'Analytics',    icon: '▲' },
  { path: '/settings',    label: 'Settings',     icon: '⚙' },
];

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const { unreadCount, markAllRead } = useNotifications();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (isMobile) {
    return (
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 60, background: 'var(--bg-surface)',
        borderTop: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        zIndex: 1000, padding: '0 8px',
      }}>
        {NAV.map((item) => (
          <NavLink key={item.path} to={item.path} style={({ isActive }) => ({
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: isActive ? 'var(--cyan)' : 'var(--text-secondary)',
            fontSize: '0.65rem', textDecoration: 'none', position: 'relative',
            flex: 1, height: '100%',
          })}>
            <span style={{ fontSize: '1.2rem', marginBottom: 2 }}>{item.icon}</span>
            <span style={{ fontWeight: 500 }}>{item.label}</span>
            {item.path === '/analytics' && unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: 4, right: '15%',
                background: 'var(--cyan)', color: '#0a0a0f',
                fontSize: '0.5rem', fontFamily: 'var(--font-mono)', fontWeight: 700,
                padding: '1px 4px', borderRadius: 10,
              }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </NavLink>
        ))}
        <button onClick={handleLogout} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: 'var(--red)', background: 'none', border: 'none',
            fontSize: '0.65rem', position: 'relative', flex: 1, height: '100%', cursor: 'pointer',
            padding: 0,
        }}>
            <span style={{ fontSize: '1.2rem', marginBottom: 2 }}>⏻</span>
            <span style={{ fontWeight: 500 }}>Logout</span>
        </button>
      </nav>
    );
  }

  return (
    <nav style={{
      width: collapsed ? 60 : 220,
      minHeight: '100vh',
      background: 'var(--bg-surface)',
      borderRight: '1px solid var(--border)',
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 250ms ease',
      flexShrink: 0,
      position: 'sticky',
      top: 0,
    }}>
      {/* Logo */}
      <div style={{ padding: collapsed ? '24px 0' : '24px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'var(--cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, fontSize: '0.9rem', fontWeight: 700, color: '#0a0a0f',
          fontFamily: 'var(--font-mono)',
        }}>ET</div>
        {!collapsed && (
          <div>
            <p style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>EmailTracker</p>
            <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>v1.0.0</p>
          </div>
        )}
      </div>

      {/* Nav Links */}
      <div style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV.map((item) => (
          <NavLink key={item.path} to={item.path} style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 12,
            padding: collapsed ? '10px 0' : '10px 12px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            borderRadius: 'var(--radius-md)',
            color: isActive ? 'var(--cyan)' : 'var(--text-secondary)',
            background: isActive ? 'var(--cyan-dim)' : 'transparent',
            fontWeight: isActive ? 600 : 400,
            fontSize: '0.85rem',
            transition: 'all 150ms',
            textDecoration: 'none',
            position: 'relative',
          })} onMouseEnter={(e) => { if (!e.currentTarget.classList.contains('active')) e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={(e) => { if (!e.currentTarget.querySelector('*')?.parentElement?.style.color?.includes('cyan')) e.currentTarget.style.background = ''; }}>
            <span style={{ fontSize: '1rem', width: 18, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
            {!collapsed && item.label}
            {item.path === '/analytics' && unreadCount > 0 && !collapsed && (
              <span style={{
                marginLeft: 'auto', background: 'var(--cyan)', color: '#0a0a0f',
                fontSize: '0.6rem', fontFamily: 'var(--font-mono)', fontWeight: 700,
                padding: '1px 6px', borderRadius: 10,
              }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </NavLink>
        ))}
      </div>

      {/* User + Collapse */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {!collapsed && user && (
          <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-elevated)', marginBottom: 4 }}>
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</p>
            <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>{user.email}</p>
          </div>
        )}
        <button onClick={() => setCollapsed((c) => !c)} style={{
          display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 8, padding: '8px 12px', borderRadius: 'var(--radius-md)',
          color: 'var(--text-muted)', fontSize: '0.8rem', width: '100%',
          cursor: 'pointer', background: 'none', border: 'none', transition: 'all 150ms',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--text-muted)'; }}>
          <span style={{ fontSize: '0.9rem' }}>{collapsed ? '→' : '←'}</span>
          {!collapsed && 'Collapse'}
        </button>
        <button onClick={handleLogout} style={{
          display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 8, padding: '8px 12px', borderRadius: 'var(--radius-md)',
          color: 'var(--red)', fontSize: '0.8rem', width: '100%',
          cursor: 'pointer', background: 'none', border: 'none', transition: 'all 150ms',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--red-dim)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
          <span>⏻</span>
          {!collapsed && 'Sign Out'}
        </button>
      </div>
    </nav>
  );
};

export const AppLayout = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  return (
    <div style={{ display: 'flex', minHeight: '100vh', flexDirection: isMobile ? 'column' : 'row' }}>
      <Sidebar />
      <main style={{ flex: 1, overflow: 'auto', background: 'var(--bg-base)', paddingBottom: isMobile ? 60 : 0 }}>
        {children}
      </main>
    </div>
  );
};
